import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadAttachment, saveBlob, formatSize, MEDIA_LABELS } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { FolderArchive, Download, Loader2, Files, HardDrive, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS = { ok: "Disponível", pending: "Recebendo", failed: "Falhou", too_large: "Muito grande", missing: "Indisponível" };

export const ChatAttachments = ({ chatId, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [zipping, setZipping] = useState(false);
  const [driveOn, setDriveOn] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/chats/${chatId}/attachments`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [chatId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/drive/status").then(({ data }) => setDriveOn(data.connected)).catch(() => {}); }, []);

  const toDrive = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/drive/upload", { ids: selected });
      if (data.failed) toast.error(`${data.ok} enviado(s), ${data.failed} com falha`);
      else toast.success(`${data.ok} arquivo(s) no Google Drive`);
      setSelected([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar ao Drive"); }
    finally { setSending(false); }
  };

  useEffect(() => { setSelected([]); }, [chatId]);

  const ready = items.filter((a) => a.status === "ok");
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = ready.length > 0 && selected.length === ready.length;

  const zip = async () => {
    setZipping(true);
    try {
      const { data } = await api.post("/attachments/zip", { ids: selected }, { responseType: "blob" });
      saveBlob(data, "anexos.zip");
    } catch { toast.error("Falha ao gerar ZIP"); }
    finally { setZipping(false); }
  };

  return (
    <div data-testid="chat-attachments-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Files className="w-3 h-3" /> Arquivos da conversa ({items.length})
        </div>
        {ready.length > 0 && (
          <button className="text-[11px] underline text-muted-foreground" data-testid="attachments-select-all"
                  onClick={() => setSelected(allSelected ? [] : ready.map((a) => a.id))}>
            {allSelected ? "Limpar" : "Selecionar todos"}
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground">Nenhum arquivo nesta conversa.</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/40 border border-border/60" data-testid="attachment-row">
              <Checkbox checked={selected.includes(a.id)} disabled={a.status !== "ok"} onCheckedChange={() => toggle(a.id)} data-testid="attachment-checkbox" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold truncate flex items-center gap-1" title={a.filename}>
                  <span className="truncate">{a.filename}</span>
                  {a.drive_link && (
                    <a href={a.drive_link} target="_blank" rel="noreferrer" title="Abrir no Google Drive" data-testid="attachment-drive-link"
                       className="shrink-0 text-sky-600 hover:text-sky-700"><HardDrive className="w-3 h-3" /></a>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {MEDIA_LABELS[a.media_type] || "Arquivo"} · {formatSize(a.size)} · {a.direction === "in" ? "Recebido" : "Enviado"} {new Date(a.created_at).toLocaleDateString("pt-BR")}
                </div>
              </div>
              {a.status === "ok" ? (
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid="attachment-row-download"
                        onClick={() => downloadAttachment(`/attachments/${a.id}/file`, a.filename).catch(() => toast.error("Falha no download"))}>
                  <Download className="w-3.5 h-3.5" />
                </Button>
              ) : (
                <Badge variant="secondary" className="text-[9px]">{STATUS[a.status] || a.status}</Badge>
              )}
            </div>
          ))}
        </div>
      )}
      {selected.length > 0 && driveOn && (
        <Button size="sm" variant="outline" onClick={toDrive} disabled={sending} className="w-full mt-2" data-testid="attachments-drive-button">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-1" /> Enviar ao Drive ({selected.length})</>}
        </Button>
      )}
      {selected.length > 0 && (
        <Button size="sm" onClick={zip} disabled={zipping} className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700" data-testid="attachments-zip-button">
          {zipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FolderArchive className="w-4 h-4 mr-1" /> Baixar ZIP ({selected.length})</>}
        </Button>
      )}
    </div>
  );
};
